import type { IndustryCodeName } from "app/pages/sales-intelligence/domain/industry/types/codes";
import type { WebsiteType } from "app/pages/sales-intelligence/domain/leads/types/enums";
import type { CrmScopeOption } from "app/pages/sales-intelligence/domain/leads/types/filter-values";
import type { Translatable } from "app/pages/sales-intelligence/types";

/**
 * Centralized i18n key generators for leads-search-filters module.
 */
const leadsSearchFiltersI18nKeys = {
    // ---- GENERAL ----

    filterBase: (filterKey: string) => `si.lead_gen_filters.${filterKey}`,

    filter: (filterKey: string) => `${leadsSearchFiltersI18nKeys.filterBase(filterKey)}.name`,
    filterTooltip: (filterKey: string) => `${leadsSearchFiltersI18nKeys.filterBase(filterKey)}.tooltip`,

    filterGroupLabel: (filterKey: string) => `si.lead_gen_filters.group.${filterKey}.label`,
    filterGroupTitle: (filterKey: string) => `si.lead_gen_filters.group.${filterKey}.title`,
    filterGroupTooltip: (filterKey: string) => `si.lead_gen_filters.group.${filterKey}.tooltip`,

    filterChip: (field: string) => `si.lead_gen_filters.${field}.chip.name`,

    filterSummaryLabel: (filterKey: string) => `si.lead_gen_filters.${filterKey}.summary.name`,

    // ---- FILTER SPECIFIC ----

    websiteTypeTaxonomy: (category: WebsiteType) =>
        `${leadsSearchFiltersI18nKeys.filterBase("websiteType")}.${category}`,

    industryFilterCategoriesTitle: "si.lead_gen_filters.group.categories.name",
    industryFilterCodesTitle: () => `${leadsSearchFiltersI18nKeys.filterBase("categoryFilter")}.codes.title`,
    industryFilterCodesAutocompletePlaceholder: (focused?: boolean) =>
        `${leadsSearchFiltersI18nKeys.filterBase("categoryFilter")}.codes.autocomplete.placeholder${focused ? ".focused" : ""}`,

    industryFilterCodesBulkAddBase: (key: string) =>
        `${leadsSearchFiltersI18nKeys.filterBase("categoryFilter")}.codes.bulkAdd.${key}`,
    industryFilterCodesBulkAddButton: () => leadsSearchFiltersI18nKeys.industryFilterCodesBulkAddBase("button"),
    industryFilterCodesBulkAddModalHeader: (codeType: IndustryCodeName): Translatable => ({
        key: leadsSearchFiltersI18nKeys.industryFilterCodesBulkAddBase("modal.header"),
        replacements: { codeType },
    }),
    industryFilterCodesBulkAddModalText: (codeType: IndustryCodeName): Translatable => ({
        key: leadsSearchFiltersI18nKeys.industryFilterCodesBulkAddBase("modal.text"),
        replacements: { codeType },
    }),
    industryFilterCodesBulkAddModalCounter: () =>
        leadsSearchFiltersI18nKeys.industryFilterCodesBulkAddBase("modal.counter"),
    industryFilterCodesBulkAddModalPlaceholder: () =>
        leadsSearchFiltersI18nKeys.industryFilterCodesBulkAddBase("modal.placeholder"),

    industryTaxonomyName: () => `${leadsSearchFiltersI18nKeys.filterBase("industry_demo")}.name`,
    industryTaxonomyTooltip: () => `${leadsSearchFiltersI18nKeys.filterBase("industry")}.tooltip`,
    industryTaxonomyDdButtonText: () => `${leadsSearchFiltersI18nKeys.filterBase("industry")}.dd_button_text`,
    industryTaxonomyDdPlaceholder: () => `${leadsSearchFiltersI18nKeys.filterBase("industry")}.dd_placeholder`,
    industryTaxonomyExpandAria: () => `${leadsSearchFiltersI18nKeys.filterBase("industry")}.expand_aria`,
    industryTaxonomyCollapseAria: () => `${leadsSearchFiltersI18nKeys.filterBase("industry")}.collapse_aria`,
    industryTaxonomyReturnToLegacy: () => `${leadsSearchFiltersI18nKeys.filterBase("industry")}.return_to_legacy`,
    industryTaxonomyLoading: () => `${leadsSearchFiltersI18nKeys.filterBase("industry")}.loading`,

    siteTagsTitle: () => `${leadsSearchFiltersI18nKeys.filterBase("categoryFilter")}.tags.label`,

    leadScoresEmptyDescription: () => `${leadsSearchFiltersI18nKeys.filterBase("leadScores")}.empty.description`,
    leadScoresEmptyCta: () => `${leadsSearchFiltersI18nKeys.filterBase("leadScores")}.empty.cta`,
    leadScoresOption: (score: string) => `${leadsSearchFiltersI18nKeys.filterBase("leadScores")}.${score}`,

    crmScopeOptionLabel: (option: CrmScopeOption) =>
        `${leadsSearchFiltersI18nKeys.filterBase("crmScope")}.${option}.label`,
    crmScopeOptionTooltip: (option: CrmScopeOption) =>
        `${leadsSearchFiltersI18nKeys.filterBase("crmScope")}.${option}.tooltip`,
    selectedFiltersLabel: "si.lead_gen_filters.selected_filters.label",
    selectedFiltersEmpty: "si.lead_gen_filters.selected_filters.empty",
    selectedFiltersCompaniesCount: "si.lead_gen_filters.selected_filters.companies_count",

    crmScopeAutoSwitchToast: "si.lead_gen_filters.crmScope.auto_switch_toast",
    leadScoresAddRule: (priority: string) =>
        `${leadsSearchFiltersI18nKeys.filterBase("leadScores")}.add_rule.${priority}`,
    leadScoresVisibilityModalTitle: () =>
        `${leadsSearchFiltersI18nKeys.filterBase("leadScores")}.visibility_modal.title`,
    leadScoresVisibilitySharedTitle: () =>
        `${leadsSearchFiltersI18nKeys.filterBase("leadScores")}.visibility_modal.shared.title`,
    leadScoresVisibilitySharedDescription: () =>
        `${leadsSearchFiltersI18nKeys.filterBase("leadScores")}.visibility_modal.shared.description`,
    leadScoresVisibilityPrivateTitle: () =>
        `${leadsSearchFiltersI18nKeys.filterBase("leadScores")}.visibility_modal.private.title`,
    leadScoresVisibilityPrivateDescription: () =>
        `${leadsSearchFiltersI18nKeys.filterBase("leadScores")}.visibility_modal.private.description`,
    leadScoresDisabledTierDescription: (priority: string) =>
        `${leadsSearchFiltersI18nKeys.filterBase("leadScores")}.disabled_tier.${priority}.description`,
    leadScoresDisabledTierCta: (priority: string) =>
        `${leadsSearchFiltersI18nKeys.filterBase("leadScores")}.disabled_tier.${priority}.cta`,
} as const;

export default leadsSearchFiltersI18nKeys;
